import { buildPseudoPressureRtaPoints, cleanGasPvt, fitPseudoPressureLinearTrend } from './rtaPvtCalculations';
import type { GasPvtPoint } from './rtaPvtCalculations';

import { loadUncertaintySettings } from './uncertaintySettings';
import React, { useMemo } from 'react';
import { ETH } from '../economics/ecoShared';
import { useProductionForecastStore } from '../../hooks/useProductionForecastStore';
import type { DeclineDataPoint } from './declineCalculations';
import { buildSensitivityModelCurves, buildSensitivityModelResults } from './sensitivityModelCalculations';
import { buildRtaPoints, fitRtaLinearTrend } from './rtaCalculations';
import { runMonteCarlo, summarizeMonteCarlo } from './uncertaintyCalculations';

export default function ForecastSummary({ projectId }: { projectId: number }) {
  const project = useProductionForecastStore((state) => state.projects[String(projectId)]);
  const uncertaintySettings = useMemo(() => loadUncertaintySettings(projectId), [projectId]);
  const declineData = useMemo<DeclineDataPoint[]>(() => {
    const rows = project?.productionRows ?? [];
    const valid = rows.map((row) => ({ date: row.date, q: Number(row.gasRate) }))
      .filter((row) => row.date && Number.isFinite(row.q) && row.q > 0)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (valid.length === 0) return [];

    const firstTime = new Date(valid[0].date).getTime();
    return valid.map((row) => ({ date: row.date, q: row.q, t: (new Date(row.date).getTime() - firstTime) / 86400000 }));
  }, [project]);

  const years = clamp(numberOrDefault(uncertaintySettings.forecastYears, 5), 0.1, 50);
  const lastT = declineData.length > 0 ? declineData[declineData.length - 1].t : 0;
  const forecastEndT = lastT + years * 365;

  const modelResults = useMemo(() => buildSensitivityModelResults(declineData, lastT, forecastEndT, 5), [declineData, lastT, forecastEndT]);

  const bestModel = useMemo(() => {
    if (modelResults.length === 0) return null;
    return [...modelResults].sort((a, b) => a.rmse - b.rmse)[0];
  }, [modelResults]);

  const declineRange = clamp(numberOrDefault(uncertaintySettings.declinePct, 20), 0, 80) / 100;
  const extendedEndT = lastT + (forecastEndT - lastT) * (1 + declineRange);

  const modelCurves = useMemo(() => buildSensitivityModelCurves(declineData, lastT, extendedEndT, numberOrDefault(uncertaintySettings.terminalDeclinePct, 5), 320), [declineData, lastT, extendedEndT, uncertaintySettings.terminalDeclinePct]);
  
  const probabilityModel = useMemo(() => modelResults.find((item) => item.key === uncertaintySettings.selectedModel) ?? modelResults[0] ?? null, [modelResults, uncertaintySettings.selectedModel]);

  const probabilityCurve = useMemo(() => {
    if (!probabilityModel) return null;
    return modelCurves.find((curve) => curve.key === probabilityModel.key) ?? null;
  }, [modelCurves, probabilityModel]);

  const monteCarlo = useMemo(() => {
    if (!probabilityCurve) return null;

    return runMonteCarlo(probabilityCurve, {
      simulations: clamp(numberOrDefault(uncertaintySettings.simulations, 1000), 100, 10000),
      displayEndT: forecastEndT,
      productivityPct: clamp(numberOrDefault(uncertaintySettings.productivityPct, 15), 0, 90),
      declinePct: clamp(numberOrDefault(uncertaintySettings.declinePct, 20), 0, 80),
      engineeringPct: clamp(numberOrDefault(uncertaintySettings.engineeringPct, 10), 0, 90),
      seed: uncertaintySettings.seed,
    });
  }, [probabilityCurve, forecastEndT, uncertaintySettings]);

  const uncertaintySummary = useMemo(() => monteCarlo ? summarizeMonteCarlo(monteCarlo) : null, [monteCarlo]);

  const reservoirPressure = Number(project?.params.reservoirPressure);

  const rtaPoints = useMemo(() => {
    if (!project || !Number.isFinite(reservoirPressure) || reservoirPressure <= 0) return [];

    const raw = project.productionRows.map((row) => ({
      date: row.date,
      q: Number(row.gasRate),
      pwf: Number(row.bottomHolePressure),
    }));

    return buildRtaPoints(raw, reservoirPressure);
  }, [project, reservoirPressure]);

  const rtaFit = useMemo(() => fitRtaLinearTrend(rtaPoints, 'sqrtT'), [rtaPoints]);

  const pvt = useMemo<GasPvtPoint[]>(() => {
    try {
      const saved = localStorage.getItem(`production_forecast_pvt_${projectId}`);
      if (!saved) return [];
      const rows = JSON.parse(saved) as Array<{ pressure?: string; z?: string; viscosity?: string }>;
      return cleanGasPvt(rows.map((row) => ({ pressure: Number(row.pressure), z: Number(row.z), viscosity: Number(row.viscosity) })).filter((point) => Number.isFinite(point.pressure) && point.pressure >= 0 && Number.isFinite(point.z) && point.z > 0 && Number.isFinite(point.viscosity) && point.viscosity > 0));
    } catch {
      return [];
    }
  }, [projectId]);

  const pvtCoverageOk = useMemo(() => {
    if (pvt.length < 2 || rtaPoints.length === 0 || !Number.isFinite(reservoirPressure)) return false;
    const minPwf = Math.min(...rtaPoints.map((point) => point.pwf));
    const minPvt = Math.min(...pvt.map((point) => point.pressure));
    const maxPvt = Math.max(...pvt.map((point) => point.pressure));
    return minPvt <= minPwf && maxPvt >= reservoirPressure;
  }, [pvt, rtaPoints, reservoirPressure]);

  const pseudoRtaPoints = useMemo(() => pvtCoverageOk ? buildPseudoPressureRtaPoints(rtaPoints, pvt) : [], [rtaPoints, pvt, pvtCoverageOk]);
  const pseudoRtaFit = useMemo(() => fitPseudoPressureLinearTrend(pseudoRtaPoints, 'sqrtT'), [pseudoRtaPoints]);

  const activeRtaFit = pseudoRtaFit ?? rtaFit;
  const rtaMethodLabel = pseudoRtaFit ? '实气拟压力 √t R²' : '压力平方 √t R²';
  
  const deliverabilityPoints = useMemo(() => {
    const points = project?.deliverability.points ?? [];
    return points.filter((point) => {
      const pr = Number(point.reservoirPressure);
      const pwf = Number(point.bottomHolePressure);
      const q = Number(point.gasRate);
      return Number.isFinite(pr) && Number.isFinite(pwf) && Number.isFinite(q) && pr > pwf && pwf > 0 && q > 0;
    });
  }, [project]);

  const maxTestRate = useMemo(() => {
    if (deliverabilityPoints.length === 0) return null;
    return Math.max(...deliverabilityPoints.map((point) => Number(point.gasRate)));
  }, [deliverabilityPoints]);

  const latestRate = declineData.length > 0 ? declineData[declineData.length - 1].q : null;

  const historicalCumulative = useMemo(() => {
    if (declineData.length < 2) return null;

    let cumulative = 0;
    for (let i = 1; i < declineData.length; i += 1) {
      const previous = declineData[i - 1];
      const current = declineData[i];
      cumulative += 0.5 * (previous.q + current.q) * Math.max(0, current.t - previous.t);
    }

    return cumulative;
  }, [declineData]);

  const futureCumulative = bestModel?.futureCumulative ?? null;
  const totalCumulative = historicalCumulative !== null && futureCumulative !== null ? historicalCumulative + futureCumulative : null;
  
  const parameterCompleteness = useMemo(() => {
    if (!project) return { valid: 0, total: 8, pct: 0 };

    const values = [
      project.params.reservoirPressure,
      project.params.effectiveThickness,
      project.params.porosity,
      project.params.permeability,
      project.params.waterSaturation,
      project.params.horizontalLength,
      project.params.fractureHalfLength,
      project.params.srv,
    ];

    const valid = values.filter((value) => value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) > 0).length;
    return { valid, total: values.length, pct: valid / values.length * 100 };
  }, [project]);

  if (!project) return <div style={{ color: ETH.textMuted }}>正在初始化综合评价...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h3 style={{ margin: 0 }}>综合评价</h3>
          <div style={{ marginTop: 5, fontSize: 11, color: ETH.textMuted }}>汇总产能试井、递减预测、RTA、敏感性和不确定性分析结果</div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: ETH.textSec }}>概率预测设置</div>
          <div style={{ marginTop: 5, fontSize: 13, fontWeight: 700 }}>{years.toFixed(1)} 年 / {uncertaintySettings.simulations} 次</div>
          <div style={{ marginTop: 3, fontSize: 10, color: ETH.textMuted }}>与“不确定性”页面保持一致</div>
        </div>
      </div>

      <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <ResultCard title="当前日产气" value={latestRate !== null ? formatWanRate(latestRate) : '--'} description="最新有效生产记录" />
        <ResultCard title="历史累计产气" value={historicalCumulative !== null ? formatWanVolume(historicalCumulative) : '--'} description="根据历史日产气梯形积分" />
        <ResultCard title="历史拟合误差最小模型" value={bestModel?.label ?? '--'} description={bestModel ? `RMSE = ${bestModel.rmse.toFixed(4)}` : '暂无有效拟合结果'} strong />
        <ResultCard title="未来确定性累计产气" value={bestModel ? formatWanVolume(bestModel.futureCumulative) : '--'} description={`${years.toFixed(1)} 年预测期`} />
      </div>

      <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <ResultCard title="P90 累计产气" value={monteCarlo ? formatWanVolume(monteCarlo.p90Cumulative) : '--'} description="概率预测保守侧" />
        <ResultCard title="P50 累计产气" value={monteCarlo ? formatWanVolume(monteCarlo.p50Cumulative) : '--'} description="概率预测中位值" strong />
        <ResultCard title="P10 累计产气" value={monteCarlo ? formatWanVolume(monteCarlo.p10Cumulative) : '--'} description="概率预测高值侧" />
        <ResultCard title="不确定性跨度" value={uncertaintySummary ? `${uncertaintySummary.spreadPct.toFixed(2)}%` : '--'} description="(P10−P90) / P50" />
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>累计产气口径</div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
          <ResultCard
            title="历史累计产气 Gp,hist"
            value={historicalCumulative !== null ? formatWanVolume(historicalCumulative) : '--'}
            description="第一条生产记录至最后一条历史记录"
          />

          <ResultCard
            title="未来累计产气 Gp,remain"
            value={futureCumulative !== null ? formatWanVolume(futureCumulative) : '--'}
            description={`历史末点之后 ${years.toFixed(1)} 年预测产量`}
          />

          <ResultCard
            title="总累计产气 Gp,total"
            value={totalCumulative !== null ? formatWanVolume(totalCumulative) : '--'}
            description="历史实际积分 + 历史末点后模型预测"
            strong
          />
        </div>
      </div>
      
      <div style={panelStyle}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>多递减模型综合对比</div>
        <div style={{ marginTop: 4, fontSize: 10, color: ETH.textMuted }}>统一预测期限下比较模型历史拟合质量和未来预测差异</div>

        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 800, borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={thStyle}>模型</th>
                <th style={thStyle}>R²</th>
                <th style={thStyle}>RMSE</th>
                <th style={thStyle}>未来累计产气（历史末点后）</th>
                <th style={thStyle}>预测末期日产气</th>
                <th style={thStyle}>历史拟合误差最小</th>
              </tr>
            </thead>

            <tbody>
              {modelResults.map((model) => (
                <tr key={model.key}>
                  <td style={tdStyle}>{model.label}</td>
                  <td style={numberTdStyle}>{model.r2.toFixed(4)}</td>
                  <td style={numberTdStyle}>{model.rmse.toFixed(4)}</td>
                  <td style={numberTdStyle}>{formatWanVolume(model.futureCumulative)}</td>
                  <td style={numberTdStyle}>{formatWanRate(model.finalRate)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: model.key === bestModel?.key ? 800 : 400 }}>{model.key === bestModel?.key ? '●' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <ResultCard title="产能试井有效点" value={`${deliverabilityPoints.length} 个`} description="满足 Pr > Pwf 且 q > 0" />
        <ResultCard title="最大测试日产气" value={maxTestRate !== null ? `${maxTestRate.toFixed(3)} ×10⁴m³/d` : '--'} description="当前产能试井有效数据" />
        <ResultCard title="RTA 有效点" value={`${rtaPoints.length} 个`} description="具有 q、Pr 和 Pwf 的生产记录" />
        <ResultCard title={rtaMethodLabel} value={activeRtaFit ? activeRtaFit.r2.toFixed(4) : '--'} description={pseudoRtaFit ? '当前采用实气拟压力 RTA 的 √t 线性一致性' : '当前采用压力平方近似 RTA 的 √t 线性一致性'} />
      </div>

      <div style={panelStyle}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>关键评价参数完整度</div>

        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 160px', gap: 18, alignItems: 'center' }}>
          <div>
            <div style={{ height: 12, background: ETH.bg, border: `1px solid ${ETH.headerBorder}`, overflow: 'hidden' }}>
              <div style={{ width: `${parameterCompleteness.pct}%`, height: '100%', background: '#b64f35' }} />
            </div>
            <div style={{ marginTop: 6, fontSize: 10, color: ETH.textMuted }}>检查地层压力、有效厚度、孔隙度、渗透率、含水饱和度、水平段长度、裂缝半长和 SRV</div>
            <div style={{ marginTop: 4, fontSize: 10, color: ETH.textMuted }}>
              该完整度不代表“数据准备”页面所有输入字段均已填写。
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 22, fontWeight: 800, fontFamily: ETH.fontMono }}>{parameterCompleteness.valid}/{parameterCompleteness.total}</div>

            <div style={{ fontSize: 10, color: ETH.textMuted }}>仅检查本次产能评价所需8项核心参数</div>
          </div>
        </div>
      </div>

      <div style={panelStyle}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>综合结果摘要</div>

        <div style={{ marginTop: 10, fontSize: 11, color: ETH.textSec, lineHeight: 1.9 }}>
          <div>· 当前共有 <b>{declineData.length}</b> 条有效生产记录，历史生产时间跨度约 <b>{lastT.toFixed(0)} d</b>。</div>

          <div>
            · 在当前已配置的递减模型中，历史拟合 RMSE 最小的是
            <b> {bestModel?.label ?? '--'} </b>
            {bestModel ? `，R² = ${bestModel.r2.toFixed(4)}，RMSE = ${bestModel.rmse.toFixed(4)}。` : '。'}
          </div>

          <div>
            · 按 <b>{years.toFixed(1)} 年</b>预测期，
            确定性未来累计产气为
            <b> {bestModel ? formatWanVolume(bestModel.futureCumulative) : '--'} </b>。
          </div>

          <div>
            · Monte Carlo 概率预测区间：
            P90 = <b>{monteCarlo ? formatWanVolume(monteCarlo.p90Cumulative) : '--'}</b>，
            P50 = <b>{monteCarlo ? formatWanVolume(monteCarlo.p50Cumulative) : '--'}</b>，
            P10 = <b>{monteCarlo ? formatWanVolume(monteCarlo.p10Cumulative) : '--'}</b>。
          </div>

          <div>
            · 当前 RTA 采用 <b>{pseudoRtaFit ? '实气拟压力' : '压力平方近似'}</b> √t 线性拟合
            {activeRtaFit ? <>，R² = <b>{activeRtaFit.r2.toFixed(4)}</b></> : '，尚缺少足够数据'}；
            该指标只描述当前数据的线性一致性，不单独作为裂缝参数唯一反演依据。
          </div>

          <div>
            · 当前关键工程参数完整度为 <b>{parameterCompleteness.valid}/{parameterCompleteness.total}</b>；
            后续正式井应用时应使用真实 PVT、实际井底流压、生产历史和经过校核的压裂参数替换当前自检数据。
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultCard({ title, value, description, strong = false }: { title: string; value: string; description: string; strong?: boolean }) {
  return (
    <div style={{ padding: 14, background: ETH.canvasBg, border: strong ? '1px solid #b64f35' : `1px solid ${ETH.headerBorder}` }}>
      <div style={{ fontSize: 10, color: ETH.textMuted }}>{title}</div>
      <div style={{ marginTop: 6, fontSize: strong ? 20 : 17, fontWeight: 800, fontFamily: ETH.fontMono }}>{value}</div>
      <div style={{ marginTop: 5, fontSize: 10, color: ETH.textMuted }}>{description}</div>
    </div>
  );
}

function numberOrDefault(value: string, fallback: number) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}



const panelStyle: React.CSSProperties = { marginTop: 14, padding: 14, background: ETH.canvasBg, border: `1px solid ${ETH.headerBorder}` };
const inputStyle: React.CSSProperties = { width: 100, padding: '6px 8px', boxSizing: 'border-box', border: `1px solid ${ETH.headerBorder}`, background: ETH.bg, color: ETH.text, outline: 'none' };
const thStyle: React.CSSProperties = { padding: '8px', borderBottom: `1px solid ${ETH.headerBorder}`, textAlign: 'center', color: ETH.textSec, whiteSpace: 'nowrap' };
const tdStyle: React.CSSProperties = { padding: '8px', borderBottom: `1px solid ${ETH.headerBorder}` };
const numberTdStyle: React.CSSProperties = { ...tdStyle, textAlign: 'right', fontFamily: ETH.fontMono, whiteSpace: 'nowrap' };
function formatWanRate(value: number, digits = 3) { return Number.isFinite(value) ? `${value.toFixed(digits)} ×10⁴m³/d` : '--'; }

function formatWanVolume(value: number, digits = 2) { return Number.isFinite(value) ? `${value.toFixed(digits)} ×10⁴m³` : '--'; }